/**
 * Editor-Ansicht.
 *
 * GrapesJS liefert die Baufläche, das MJML-Plugin die e-mail-sicheren
 * Komponenten. Der eigene Anteil:
 *  - Kanzlei-Bausteine, die die Gestaltung bereits mitbringen
 *  - die Bildbibliothek mit Ziehen und Ablegen
 *  - bearbeitbare Attribute (Farbe, Größe) statt nur "Id" und "Titel"
 *  - das Speichern als MJML, damit eine Vorlage bearbeitbar bleibt
 *  - der Prüfbericht, der Outlook-Probleme beim Bauen zeigt statt beim
 *    Empfänger
 */

import grapesjs from 'grapesjs';
import mjmlPlugin from 'grapesjs-mjml';
import 'grapesjs/dist/css/grapes.min.css';
import { Vorlagen, Status } from '../api.js';
import { el, ersetzen, knopf, meldung, eingabefeld, dialog, laedt } from '../ui.js';
import { bausteineFuer, startvorlageFuer } from '../ci-bausteine.js';
import { einstellungenErgaenzen } from '../editor-einstellungen.js';
import { bildbereich } from '../bildbibliothek.js';
import { einfuegeFilterAnhaengen } from '../einfuege-filter.js';
import { symbolFuer } from '../baustein-symbole.js';
import { bausteineAufraeumen } from '../baustein-uebersetzung.js';
import { mjmlNormalisieren } from '../mjml-normalisieren.js';
import {
  eigeneBausteineLaden,
  abschnittSpeichern,
  textbausteinBereich,
} from '../eigene-bausteine.js';
import { empfaengerVorschau } from '../empfaenger-vorschau.js';
import { verlassenSchuetzen } from '../verlassen-schutz.js';
import { bausteinEinsetzen } from '../baustein-einfuegen.js';
import {
  bildImTextRegistrieren,
  bildInTextEinsetzen,
  textbausteinFinden,
  anteilFuerNaturbreite,
} from '../bild-im-text.js';
import '../editor-stil.css';

/**
 * @returns {import('../app.js').Ansicht}
 */
export function editorAnsicht() {
  return {
    titel: 'Editor',
    async zeichnen(behaelter, parameter) {
      const vorlagenId = parameter.id ? Number(parameter.id) : null;

      // Die Basisadresse bestimmt, unter welcher Adresse die Bilder im
      // Postfach des Empfaengers erreichbar sind.
      const status = await Status.laden().catch(() => null);
      const basisUrl = status?.basisUrl ?? location.origin;
      const BAUSTEINE = bausteineFuer(basisUrl);

      /** @type {{ id: number|null, name: string, betreff: string, vorschautext: string, mjml: string }} */
      const vorlage = {
        id: vorlagenId,
        name: 'Neue Vorlage',
        betreff: '',
        vorschautext: '',
        mjml: startvorlageFuer(basisUrl),
      };

      if (vorlagenId) {
        const geladen = await Vorlagen.laden(vorlagenId);
        vorlage.name = geladen.name;
        vorlage.betreff = geladen.betreff ?? '';
        vorlage.vorschautext = geladen.vorschautext ?? '';
        vorlage.mjml = geladen.mjml;
      } else {
        // Aus der Galerie gewählte Vorlage, vom Vorlagenbereich hinterlegt.
        const ausGalerie = sessionStorage.getItem('galerie-mjml');
        if (ausGalerie) {
          vorlage.mjml = ausGalerie;
          vorlage.name = sessionStorage.getItem('galerie-name') ?? 'Neue Vorlage';
          vorlage.betreff = sessionStorage.getItem('galerie-betreff') ?? '';
          sessionStorage.removeItem('galerie-mjml');
          sessionStorage.removeItem('galerie-name');
          sessionStorage.removeItem('galerie-betreff');
        }
      }

      const nameAnzeige = el('span', { text: vorlage.name });
      const befundBereich = el('div.editor-befunde');
      const leinwand = el('div.editor-leinwand');
      const bloeckeBereich = el('div#bloecke.editor-bereich');

      // Der Hinweis sitzt FEST unter den Reitern, nicht in der scrollenden
      // Liste: dort wäre er nach dem ersten Scrollen weg — und Klicken ist
      // der neue, unbekannte Weg. Ziehen konnte man vorher schon, nur eben
      // nicht zuverlässig in eine schmale Spalte.
      const bausteinHinweis = el('p.editor-hinweis.bausteine-hinweis', {
        text: 'Baustein anklicken setzt ihn an der ausgewählten Stelle ein. Ziehen geht weiterhin.',
      });
      const bilderBereich = el('div.editor-bereich.versteckt');
      const einstellungenBereich = el('div.editor-bereich', [
        el('p.editor-hinweis', {
          text: 'Klicken Sie einen Baustein an, um Text, Farbe, Größe und Abstände zu ändern.',
        }),
        el('div#einstellungen'),
      ]);

      const textBereich = el('div.editor-bereich.versteckt');

      const reiterBausteine = el('button.aktiv', { type: 'button', text: 'Bausteine' });
      const reiterBilder = el('button', { type: 'button', text: 'Bilder' });
      const reiterTexte = el('button', { type: 'button', text: 'Textbausteine' });

      /** @param {HTMLElement} aktiverReiter @param {HTMLElement} aktiverBereich */
      const umschalten = (aktiverReiter, aktiverBereich) => {
        for (const [reiter, bereich] of [
          [reiterBausteine, bloeckeBereich],
          [reiterBilder, bilderBereich],
          [reiterTexte, textBereich],
        ]) {
          reiter.classList.toggle('aktiv', reiter === aktiverReiter);
          bereich.classList.toggle('versteckt', bereich !== aktiverBereich);
        }
        // Der Hinweis gilt nur für die Bausteine.
        bausteinHinweis.classList.toggle('versteckt', aktiverBereich !== bloeckeBereich);
      };

      reiterBausteine.addEventListener('click', () => umschalten(reiterBausteine, bloeckeBereich));
      reiterBilder.addEventListener('click', () => umschalten(reiterBilder, bilderBereich));
      reiterTexte.addEventListener('click', () => umschalten(reiterTexte, textBereich));

      // Umschaltung zwischen Rechner- und Telefonansicht. GrapesJS bringt
      // dafür eine eigene Leiste mit, die aber im dunklen Fremdstil steckt -
      // hier sitzt sie bei den übrigen Bedienelementen.
      const geraeteumschaltung = el('div.geraete');
      /** @type {HTMLButtonElement[]} */
      const geraeteKnoepfe = [];
      // Der interne Gerätename bleibt, die Beschriftung sagt, was gemeint ist:
      // „Rechner" liest sich wie eine Funktion, nicht wie eine Ansicht.
      for (const [name, beschriftung] of [
        ['Rechner', 'Am Computer'],
        ['Telefon', 'Am Handy'],
      ]) {
        const knopfElement = /** @type {HTMLButtonElement} */ (
          el(`button${name === 'Rechner' ? '.aktiv' : ''}`, {
            type: 'button',
            text: beschriftung,
            onclick: () => {
              editor.setDevice(name);
              geraeteKnoepfe.forEach((k) => k.classList.toggle('aktiv', k === knopfElement));
            },
          })
        );
        geraeteKnoepfe.push(knopfElement);
        geraeteumschaltung.append(knopfElement);
      }

      // Rückgängig und Wiederherstellen. GrapesJS führt den Verlauf ohnehin
      // mit — es fehlten nur die Bedienelemente. Ohne sie traut sich niemand,
      // etwas auszuprobieren, und genau das braucht ein Gestaltungswerkzeug.
      const zurueckKnopf = /** @type {HTMLButtonElement} */ (
        el('button.verlauf', {
          type: 'button',
          title: 'Rückgängig (Strg+Z)',
          'aria-label': 'Rückgängig',
          text: '↶',
          onclick: () => {
            editor.UndoManager.undo();
            verlaufAktualisieren();
          },
        })
      );
      const vorKnopf = /** @type {HTMLButtonElement} */ (
        el('button.verlauf', {
          type: 'button',
          title: 'Wiederherstellen (Strg+Umschalt+Z)',
          'aria-label': 'Wiederherstellen',
          text: '↷',
          onclick: () => {
            editor.UndoManager.redo();
            verlaufAktualisieren();
          },
        })
      );

      /**
       * Graut die Knöpfe aus, wenn es nichts zu tun gibt.
       *
       * Ein Knopf, der bedienbar aussieht und nichts bewirkt, ist schlimmer
       * als keiner — man drückt ihn und zweifelt am Werkzeug.
       */
      function verlaufAktualisieren() {
        zurueckKnopf.disabled = !editor.UndoManager.hasUndo();
        vorKnopf.disabled = !editor.UndoManager.hasRedo();
      }

      const linkeSpalte = el('aside.editor-links', [
        el('div.editor-reiter', [reiterBausteine, reiterBilder, reiterTexte]),
        bausteinHinweis,
        bloeckeBereich,
        bilderBereich,
        textBereich,
      ]);

      const rechteSpalte = el('aside.editor-rechts', [
        el('div.editor-reiter', [el('button.aktiv', { type: 'button', text: 'Einstellungen' })]),
        einstellungenBereich,
      ]);

      /**
       * Schubladen für schmale Bildschirme.
       *
       * Unter 960 px stehen die beiden Spalten übereinander statt nebeneinander
       * — sie werden über diese Knöpfe ein- und ausgeblendet. Auf breiten
       * Schirmen sind die Knöpfe unsichtbar (CSS), weil dort beide Spalten
       * ohnehin offen stehen.
       *
       * @param {'links'|'rechts'} seite
       */
      const schubladeUmschalten = (seite) => {
        const spalten = { links: linkeSpalte, rechts: rechteSpalte };
        const ziel = spalten[seite];
        const andere = seite === 'links' ? rechteSpalte : linkeSpalte;
        andere.classList.remove('offen');
        ziel.classList.toggle('offen');
      };

      const bausteineSchublade = knopf('Bausteine', {
        art: 'still',
        klasse: 'nur-schmal',
        titel: 'Bausteinauswahl ein- und ausblenden',
        beiKlick: () => schubladeUmschalten('links'),
      });
      const einstellungenSchublade = knopf('Einstellungen', {
        art: 'still',
        klasse: 'nur-schmal',
        titel: 'Einstellungen ein- und ausblenden',
        beiKlick: () => schubladeUmschalten('rechts'),
      });

      const werkzeugleiste = el('div.editor-leiste', [
        el('div.editor-titel', [
          knopf('‹ Vorlagen', {
            art: 'still',
            beiKlick: () => {
              location.hash = '#/vorlagen';
            },
          }),
          nameAnzeige,
        ]),
        el('div.knopf-reihe', [
          bausteineSchublade,
          einstellungenSchublade,
          el('div.geraete', [zurueckKnopf, vorKnopf]),
          geraeteumschaltung,
          knopf('Als Baustein', {
            art: 'still',
            beiKlick: () =>
              abschnittSpeichern(editor, (m) => ersetzen(befundBereich, m)),
          }),
          knopf('Vorschau', { art: 'still', beiKlick: () => vorschauZeigen() }),
          knopf('Prüfen', { beiKlick: () => pruefen() }),
          knopf('Speichern', { art: 'haupt', beiKlick: () => speichern() }),
        ]),
      ]);

      ersetzen(
        behaelter,
        el('div.bahn.breit.bahn-editor', [
          el('div.editor', [
            werkzeugleiste,
            el('div.editor-flaeche', [linkeSpalte, leinwand, rechteSpalte]),
            befundBereich,
          ]),
        ]),
      );

      const editor = grapesjs.init({
        container: leinwand,
        height: '100%',
        width: 'auto',
        fromElement: false,
        // Die Wahrheit liegt in der Datenbank. Ein lokaler Zwischenstand
        // würde stillschweigend vom Server abweichen.
        storageManager: false,
        plugins: [mjmlPlugin],
        blockManager: { appendTo: bloeckeBereich },
        traitManager: { appendTo: '#einstellungen' },
        panels: { defaults: [] },
        // Der Bild-Ablagedialog von GrapesJS würde base64 erzeugen - genau
        // das, was in E-Mails nicht funktioniert. Bilder kommen deshalb
        // ausschließlich aus der eigenen Bibliothek.
        assetManager: { custom: true },
        deviceManager: {
          devices: [
            { name: 'Rechner', width: '' },
            { name: 'Telefon', width: '320px', widthMedia: '480px' },
          ],
        },
        i18n: {
          locale: 'de',
          messages: {
            de: {
              traitManager: {
                empty: 'Wählen Sie einen Baustein aus.',
                label: 'Einstellungen',
                traits: {
                  labels: {
                    href: 'Verweisziel',
                    alt: 'Bildbeschreibung',
                    title: 'Titel',
                    src: 'Bildadresse',
                    id: 'Kennung',
                  },
                },
              },
              blockManager: { labels: {}, categories: { Kanzlei: 'Fertige Abschnitte' } },
            },
          },
        },
      });

      // Bearbeitbare Attribute nachrüsten - MUSS vor setComponents laufen,
      // damit die geladene Vorlage die erweiterten Typen bekommt.
      einstellungenErgaenzen(editor);

      // Bilder IM Text: dasselbe Argument, derselbe Zeitpunkt. Ohne diese
      // Anmeldung wäre ein umflossenes Bild aus einer gespeicherten Vorlage
      // nur ein Stück Text - nicht anklickbar, ohne eigene Einstellungen.
      bildImTextRegistrieren(editor);

      // Text aus Word oder Outlook wird beim Einfügen von seiner Gestaltung
      // befreit - sonst brächte jede kopierte Zeile fremde Schriften und
      // Farben in die Vorlage.
      einfuegeFilterAnhaengen(editor);

      // Kanzlei-Bausteine ganz oben, damit sie zuerst gesehen werden.
      for (const baustein of BAUSTEINE) {
        editor.BlockManager.add(baustein.id, {
          label: baustein.beschriftung,
          category: baustein.kategorie,
          content: baustein.mjml,
          // Eine Zeichnung des Aufbaus sagt mehr als der Name allein.
          media: symbolFuer(baustein.id),
        });
      }

      // Die Bausteine des Plugins uebersetzen, bebildern und ausduennen -
      // danach, damit die Kanzlei-Bausteine bereits eingetragen sind.
      bausteineAufraeumen(editor);

      // Die fertigen Abschnitte nach oben. Die Auswahl sortiert ihre Gruppen
      // in der Reihenfolge, in der das Plugin sie angelegt hat - und das sind
      // die Grundelemente zuerst. Für die tägliche Arbeit ist der fertige
      // Abschnitt aber der Regelfall.
      const gruppen = [...bloeckeBereich.querySelectorAll('.gjs-block-category')];
      const kanzleiGruppe = gruppen.find((gruppe) =>
        gruppe.querySelector('.gjs-title')?.textContent?.includes('Fertige Abschnitte'),
      );
      if (kanzleiGruppe?.parentElement) {
        kanzleiGruppe.parentElement.prepend(kanzleiGruppe);
      }

      // --- Klick statt Ziehen ------------------------------------------------
      //
      // Ziehen war bisher der einzige Weg, einen Baustein zu setzen. In eine
      // zweite Spalte ließ sich damit nichts einfügen: eine schmale Spalte ist
      // ein winziges Ziel, und wer knapp danebentrifft, bekommt seinen
      // Baustein als neuen Abschnitt ganz unten. Ein Klick ist eindeutig.
      bloeckeBereich.addEventListener('click', (ereignis) => {
        const ziel = /** @type {HTMLElement} */ (ereignis.target);
        const kachel = ziel.closest?.('.gjs-block');
        if (!kachel) return;

        // Über die Beschriftung, nicht über die Position: die Reihenfolge im
        // DOM folgt den Kategorien und stimmt nicht mit der Modellreihenfolge
        // überein, sobald umsortiert wurde.
        const beschriftung = kachel.textContent?.trim() ?? '';
        const alle = editor.BlockManager.getAll();
        const baustein =
          alle.find((b) => (b.get('label') ?? '').trim() === beschriftung) ??
          alle.at([...bloeckeBereich.querySelectorAll('.gjs-block')].indexOf(kachel));
        if (!baustein) return;

        const ergebnis = bausteinEinsetzen(editor, baustein);
        if (!ergebnis) return;
        ersetzen(
          befundBereich,
          meldung('erfolg', `„${baustein.get('label')}" eingesetzt — ${ergebnis.beschreibung}.`),
        );
      });

      // Normalisiert, weil GrapesJS mit der HTML-Verarbeitung des Browsers
      // parst: die kennt `mj-image` nicht als inhaltloses Element, liest
      // `<mj-image ... />` als geoeffnetes Tag und macht ALLE folgenden
      // Bausteine zu seinen Kindern. Die Vorlage waere danach zerstoert -
      // sichtbar erst beim Pruefen, mit einer Meldung des MJML-Compilers.
      editor.setComponents(mjmlNormalisieren(vorlage.mjml));

      // Der Verlauf beginnt beim geladenen Zustand — was davor liegt, gehört
      // nicht dazu. Sonst könnte man die Vorlage „vor das Laden" zurücknehmen
      // und stünde vor einer leeren Fläche.
      // Ein Klick in die Arbeitsfläche schließt eine offene Schublade. Sonst
      // verdeckt sie genau das, woran gearbeitet wird, und man muss erst den
      // Knopf oben wiederfinden.
      leinwand.addEventListener(
        'pointerdown',
        () => {
          linkeSpalte.classList.remove('offen');
          rechteSpalte.classList.remove('offen');
        },
        true,
      );

      // Im Entwicklungsbetrieb erreichbar, damit sich der Zustand des Editors
      // von aussen ansehen laesst - ohne diese Referenz ist jede Diagnose ein
      // Ratespiel ueber das gerenderte HTML. Im gebauten Stand fehlt sie.
      if (import.meta.env.DEV) {
        /** @type {any} */ (window).__editor = editor;
      }

      editor.UndoManager.clear();
      verlaufAktualisieren();
      editor.on('update', verlaufAktualisieren);
      editor.on('component:update', verlaufAktualisieren);

      // --- Schutz vor Arbeitsverlust ----------------------------------------
      //
      // Ein Klick auf „Empfänger" in der Seitenleiste hat die halbfertige
      // Vorlage bisher ersatzlos verworfen — ohne Rückfrage. Genau so verliert
      // man eine Stunde Arbeit und merkt es erst beim Zurückkommen.
      let ungespeichert = false;

      // Das Laden selbst löst noch Ereignisse aus; erst danach zählt eine
      // Änderung als Änderung. Sonst gilt jede frisch geöffnete Vorlage
      // sofort als bearbeitet und fragt beim Weggehen unnötig nach.
      let aenderungenZaehlen = false;
      setTimeout(() => {
        aenderungenZaehlen = true;
      }, 500);

      const alsGeaendertMerken = () => {
        if (aenderungenZaehlen) ungespeichert = true;
      };
      editor.on('update', alsGeaendertMerken);
      editor.on('component:update', alsGeaendertMerken);

      /** Nach dem Speichern ist der Stand wieder sicher. */
      const alsGesichertMerken = () => {
        ungespeichert = false;
      };

      verlassenSchuetzen(() =>
        ungespeichert
          ? `Die Vorlage „${vorlage.name || 'Neue Vorlage'}" hat Änderungen, die noch nicht gespeichert sind.`
          : null,
      );

      /**
       * Setzt ein Bild in die Nachricht ein.
       *
       * Ein Klick MUSS etwas bewirken. Die frühere Fassung verlangte, vorher
       * einen Bildbaustein auszuwählen, und belehrte sonst — für die
       * Bedienerin sah das schlicht so aus, als sei das Werkzeug kaputt.
       * Jetzt entscheidet das Werkzeug selbst, wo das Bild hingehört:
       *
       *   - Bildbaustein ausgewählt  → dessen Bild austauschen
       *   - anderer Baustein         → neues Bild direkt darunter einfügen
       *   - nichts ausgewählt        → ans Ende der Nachricht anhängen
       *
       * Bei `text-links`/`text-rechts` geht das Bild stattdessen IN den Text
       * und wird umflossen. Das ist der einzige Weg, in einer E-Mail ein Bild
       * neben laufenden Text zu stellen — siehe bild-im-text.js.
       *
       * @param {string} adresse
       * @param {string} beschriftung
       * @param {number} [naturbreite] Für die width-Angabe, die Outlook braucht.
       * @param {string} [art] 'block' | 'text-links' | 'text-rechts'
       */
      function bildEinsetzen(adresse, beschriftung, naturbreite, art = 'block') {
        const gewaehlt = editor.getSelected();

        if (art === 'text-links' || art === 'text-rechts') {
          const textbaustein = textbausteinFinden(editor);
          if (!textbaustein) {
            // Kein Belehren ohne Ausweg: der Satz nennt den einen Handgriff,
            // der fehlt, statt nur festzustellen, dass etwas nicht geht.
            ersetzen(
              befundBereich,
              meldung(
                'warnung',
                'Ein Bild im Text braucht einen Text. Bitte zuerst den Absatz anklicken, ' +
                  'in den das Bild soll — oder „Eigene Zeile" wählen.',
              ),
            );
            return;
          }

          const anordnung = art === 'text-links' ? 'left' : 'right';
          const stelle = bildInTextEinsetzen(editor, textbaustein, {
            adresse,
            beschriftung,
            anordnung,
            anteil: anteilFuerNaturbreite(naturbreite),
          });
          if (!stelle) return;

          ersetzen(
            befundBereich,
            meldung(
              'erfolg',
              `Bild „${beschriftung}" ${anordnung === 'left' ? 'links' : 'rechts'} in den Text ` +
                `gesetzt${stelle === 'cursor' ? '' : ' (an den Anfang des Absatzes)'}. ` +
                'Anklicken ändert Seite und Breite.',
            ),
          );
          return;
        }

        if (gewaehlt?.get('type') === 'mj-image') {
          gewaehlt.addAttributes({ src: adresse, alt: beschriftung });
          ersetzen(befundBereich, meldung('erfolg', `Bild „${beschriftung}" ausgetauscht.`));
          return;
        }

        // 600 px ist die Nachrichtenbreite; schmalere Bilder behalten ihre
        // eigene Breite, damit ein Logo nicht auf volle Breite gezogen wird.
        const breite = Math.min(naturbreite || 600, 600);
        const mjml =
          `<mj-image src="${adresse}" width="${breite}px" ` +
          `alt="${beschriftung.replace(/"/g, '')}" padding="10px 0"></mj-image>`;

        // Direkt unter dem ausgewählten Baustein einfügen - dort erwartet man
        // es, wenn man vorher irgendwo hingeklickt hat.
        const ziel = gewaehlt?.parent();
        if (ziel && typeof ziel.append === 'function') {
          const stelle = ziel.components().indexOf(gewaehlt) + 1;
          ziel.append(mjml, { at: stelle });
          ersetzen(befundBereich, meldung('erfolg', `Bild „${beschriftung}" eingefügt.`));
          return;
        }

        // Sonst: in die erste Spalte der Nachricht, ans Ende.
        const spalte = editor.getWrapper()?.find('[data-gjs-type="mj-column"]')?.[0];
        if (spalte) {
          spalte.append(mjml);
          ersetzen(befundBereich, meldung('erfolg', `Bild „${beschriftung}" ans Ende gesetzt.`));
          return;
        }

        ersetzen(
          befundBereich,
          meldung(
            'warnung',
            'In dieser Vorlage gibt es noch keine Spalte, in die das Bild passt. ' +
              'Bitte zuerst einen Abschnitt aus der Bausteinauswahl einfügen.',
          ),
        );
      }

      // Bildbibliothek: ein Klick setzt das Bild ein - immer.
      bilderBereich.append(
        bildbereich({
          beiAuswahl: bildEinsetzen,
        }),
      );

      /**
       * Liest das MJML aus dem Editor.
       * @returns {string}
       */
      function mjmlLesen() {
        // Achtung: `mjml-get-code` ist im Plugin NICHT registriert (dort heisst
        // das Kommando `mjml-code`). Der Aufruf liefert deshalb immer
        // undefined, und tatsaechlich arbeitet seit jeher der Rueckfallweg -
        // `editor.getHtml()`, das die mj-*-Komponenten als MJML serialisiert.
        // Nachgeprueft am 2026-08-18 im laufenden Werkzeug: gespeichert wird
        // gueltiges MJML. Der Aufruf bleibt stehen, falls das Plugin den Namen
        // nachreicht; verlassen sollte man sich nicht auf ihn.
        const ergebnis = editor.runCommand('mjml-get-code');
        if (typeof ergebnis === 'string') return ergebnis;
        return ergebnis?.mjml ?? editor.getHtml();
      }

      // Textbausteine: ein Klick setzt den Text beim ausgewählten Baustein ein.
      textBereich.append(
        textbausteinBereich({
          beiAuswahl: (text) => {
            const gewaehlt = editor.getSelected();
            if (!gewaehlt || gewaehlt.get('type') !== 'mj-text') {
              ersetzen(
                befundBereich,
                meldung(
                  'info',
                  'Bitte zuerst den Textbaustein in der Nachricht anklicken, in den der Text soll.',
                ),
              );
              return;
            }
            // An den vorhandenen Text anhängen statt ihn zu ersetzen: eine
            // Grußformel kommt unter den Fließtext, nicht an seine Stelle.
            //
            // Über `components()`, nicht über `set('content')`: Letzteres
            // ändert nur das Modell, ohne die Bearbeitungsfläche neu zu
            // zeichnen. Die Meldung stünde dann da, die Änderung wäre
            // unsichtbar — und beim Speichern plötzlich doch vorhanden.
            const bisher = gewaehlt.getInnerHTML?.() ?? gewaehlt.get('content') ?? '';
            const alsAbsaetze = text
              .split(/\n{2,}/)
              .map((teil) => `<p>${teil.replace(/\n/g, '<br>')}</p>`)
              .join('');
            gewaehlt.components(`${bisher}${alsAbsaetze}`);
            ersetzen(befundBereich, meldung('erfolg', 'Textbaustein eingesetzt.'));
          },
        }),
      );

      // Die eigene Sammlung der Kanzlei in die Bausteinauswahl eintragen.
      await eigeneBausteineLaden(editor);

      /**
       * @param {{schwere: string, nachricht: string, hinweis?: string}[]} befunde
       * @param {boolean} blockiert
       * @param {{ nachsatz?: HTMLElement|null }} [zusatz]
       */
      function befundeAnzeigen(befunde, blockiert, zusatz = {}) {
        const nachsatz = zusatz.nachsatz ?? null;

        if (!befunde.length) {
          ersetzen(
            befundBereich,
            meldung('erfolg', 'Keine Beanstandungen — die Vorlage ist versandfähig.'),
            ...(nachsatz ? [nachsatz] : []),
          );
          return;
        }
        const fehler = befunde.filter((b) => b.schwere === 'fehler').length;
        const warnungen = befunde.length - fehler;

        ersetzen(
          befundBereich,
          meldung(
            blockiert ? 'fehler' : 'warnung',
            blockiert
              ? `${fehler === 1 ? 'Ein Punkt verhindert' : `${fehler} Punkte verhindern`} den Versand` +
                `${warnungen ? `, dazu ${warnungen === 1 ? 'ein Hinweis' : `${warnungen} Hinweise`}` : ''}.`
              : `${warnungen === 1 ? 'Ein Hinweis' : `${warnungen} Hinweise`} — der Versand ist möglich.`,
          ),
          el(
            'ul.befund-liste',
            befunde.map((befund) =>
              el(`li.${befund.schwere}`, [
                el('strong', { text: befund.nachricht }),
                befund.hinweis ? el('span', { text: befund.hinweis }) : null,
                // Ein Befund, der HTML zum Abtippen vorschlägt, ist für eine
                // Sachbearbeiterin keine Anleitung. Wo das Werkzeug den Mangel
                // selbst beheben kann, gehört dort ein Knopf hin.
                befund.regel === 'abmeldelink-fehlt'
                  ? el('span', { style: 'display:block;margin-top:.5rem' }, [
                      knopf('Fußzeile mit Abmeldelink einfügen', {
                        titel: 'Setzt die Kanzlei-Fußzeile samt Abmeldelink ans Ende der Vorlage',
                        beiKlick: () => fusszeileEinfuegen(),
                      }),
                    ])
                  : null,
              ]),
            ),
          ),
          ...(nachsatz ? [nachsatz] : []),
        );

        // Der Befundkasten sitzt unterhalb der Arbeitsfläche. Ohne diesen
        // Griff wirkt ein Klick auf „Prüfen" folgenlos: das Ergebnis steht
        // außerhalb des Sichtfelds, und man hält das Werkzeug für kaputt.
        befundBereich.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      /**
       * Setzt die Kanzlei-Fußzeile mit Abmeldelink ans Ende der Vorlage.
       *
       * Ohne Abmeldelink darf nichts hinausgehen - das ist keine Formalie,
       * sondern die Bedingung dafür, dass die Aussendung rechtmäßig ist und
       * bei Gmail und Yahoo nicht abgewertet wird.
       */
      function fusszeileEinfuegen() {
        const fuss = BAUSTEINE.find((baustein) => baustein.id === 'kanzlei-fuss');
        if (!fuss) return;

        // In den Nachrichtenkörper, nicht an den Wrapper: ein Abschnitt neben
        // mj-body ergibt kein gültiges MJML, und die Prüfung danach scheitert
        // an einer Vorlage, die das Werkzeug selbst zerlegt hat.
        const koerper = editor.getWrapper()?.find('[data-gjs-type="mj-body"]')?.[0];
        if (koerper && typeof koerper.append === 'function') {
          koerper.append(fuss.mjml);
        } else {
          editor.addComponents(fuss.mjml);
        }
        ersetzen(
          befundBereich,
          meldung('erfolg', 'Fußzeile mit Abmeldelink eingefügt. Bitte erneut prüfen.'),
        );
      }

      /**
       * Prüft in zwei Stufen.
       *
       * Zuerst das, was ohne Netz zu haben ist — das Ergebnis steht sofort da.
       * Dann der Abruf jeder Bildadresse und jedes Verweises: er dauert
       * Sekunden, findet aber genau den Fehler, den keine Quelltextprüfung
       * sehen kann — ein Bild, das im Editor noch aus dem Zwischenspeicher des
       * Browsers kam und im Postfach des Empfängers fehlt.
       *
       * @param {{ netz?: boolean }} [optionen]
       */
      async function pruefen({ netz = true } = {}) {
        ersetzen(befundBereich, laedt('Vorlage wird geprüft …'));
        const mjml = mjmlLesen();

        try {
          const schnell = await Vorlagen.vorschau({ mjml, betreff: vorlage.betreff || undefined });
          befundeAnzeigen(schnell.befunde, schnell.blockiert, {
            nachsatz: netz ? laedt('Bilder und Verweise werden abgerufen …') : null,
          });
          if (!netz) return;
        } catch (fehler) {
          // Trägt der Fehler Befunde (etwa eine nicht übersetzbare Vorlage),
          // gehören die angezeigt: sie benennen die Stelle. Eine nackte
          // Fehlermeldung ließe die Bedienerin ratlos zurück.
          const mitBefunden = /** @type {any} */ (fehler)?.details;
          if (Array.isArray(mitBefunden) && mitBefunden.length) {
            befundeAnzeigen(mitBefunden, true);
            return;
          }
          ersetzen(
            befundBereich,
            meldung('fehler', fehler instanceof Error ? fehler.message : 'Prüfung fehlgeschlagen'),
          );
          return;
        }

        try {
          const voll = await Vorlagen.zustellung({ mjml, betreff: vorlage.betreff || undefined });
          befundeAnzeigen(voll.befunde, voll.blockiert, { nachsatz: groessenzeile(voll) });
        } catch (fehler) {
          // Die schnelle Prüfung steht schon — sie darf nicht verschwinden,
          // nur weil der Abruf gescheitert ist. Der Grund kommt dazu.
          befundBereich.append(
            meldung(
              'warnung',
              `Bilder und Verweise konnten nicht geprüft werden: ${
                fehler instanceof Error ? fehler.message : 'Abruf fehlgeschlagen'
              }`,
            ),
          );
        }
      }

      /**
       * Die Ladelast der fertigen Nachricht — die Zahl, die im Postfach zählt.
       *
       * @param {{ groesse?: { htmlBytes: number, bildBytes: number, gesamtBytes: number, vollstaendig: boolean } }} ergebnis
       * @returns {HTMLElement|null}
       */
      function groessenzeile(ergebnis) {
        const groesse = ergebnis.groesse;
        if (!groesse?.gesamtBytes) return null;

        const kb = (wert) => `${Math.round(wert / 1024)} KB`;
        return el('p.editor-hinweis', {
          text:
            `Ladelast der Nachricht: ${kb(groesse.gesamtBytes)}` +
            `${groesse.vollstaendig ? '' : ' (mindestens)'} — ` +
            `Text ${kb(groesse.htmlBytes)}, Bilder ${kb(groesse.bildBytes)}.`,
        });
      }

      /**
       * Zeigt die Nachricht so, wie echte Empfänger sie bekommen.
       *
       * Der Musterempfänger im Editor hat immer alle Felder gefüllt — die
       * Lücken, die im Alltag Ärger machen, sieht man nur an echten Daten.
       */
      function vorschauZeigen() {
        const ansicht = empfaengerVorschau({
          mjmlLesen,
          betreffLesen: () => vorlage.betreff,
        });
        const schliessen = dialog({
          titel: 'Vorschau mit echten Empfängern',
          breit: true,
          inhalt: [ansicht.element],
          aktionen: [knopf('Schließen', { beiKlick: () => schliessen() })],
        });
      }

      async function speichern() {
        const { feld: nameFeld, eingabe: nameEingabe } = eingabefeld({
          beschriftung: 'Name der Vorlage',
          wert: vorlage.name,
          hinweis: 'Nur für Ihre Übersicht — die Empfänger sehen ihn nicht.',
        });
        const { feld: betreffFeld, eingabe: betreffEingabe } = eingabefeld({
          beschriftung: 'Betreffzeile (Vorschlag)',
          wert: vorlage.betreff,
          platzhalter: 'Kann bei jeder Aussendung angepasst werden',
        });
        const { feld: vorschauFeld, eingabe: vorschauEingabe } = eingabefeld({
          beschriftung: 'Vorschautext',
          wert: vorlage.vorschautext,
          platzhalter: 'Die Fristen im Herbst — und was jetzt zu tun ist',
          hinweis:
            'Steht im Posteingang neben dem Betreff. Bleibt er leer, zeigen Outlook und Gmail dort ' +
            'den Anfang der Nachricht — also die Anrede. Rund 90 Zeichen sind sichtbar.',
        });

        const meldungsBereich = el('div');

        const schliessen = dialog({
          titel: 'Vorlage speichern',
          inhalt: [nameFeld, betreffFeld, vorschauFeld, meldungsBereich],
          aktionen: [
            knopf('Abbrechen', { beiKlick: () => schliessen() }),
            knopf('Speichern', {
              art: 'haupt',
              beiKlick: async () => {
                const daten = {
                  name: nameEingabe.value.trim() || 'Neue Vorlage',
                  mjml: mjmlLesen(),
                  betreff: betreffEingabe.value.trim(),
                  vorschautext: vorschauEingabe.value.trim(),
                  entwurfJson: JSON.stringify(editor.getProjectData()),
                };
                ersetzen(meldungsBereich, laedt('Wird gespeichert …'));
                try {
                  if (vorlage.id) {
                    await Vorlagen.speichern(vorlage.id, daten);
                  } else {
                    const neu = await Vorlagen.anlegen(daten);
                    vorlage.id = neu.id;
                    // Adresszeile mitführen, damit ein Neuladen die Vorlage behält.
                    history.replaceState(null, '', `#/editor/${neu.id}`);
                  }
                  vorlage.name = daten.name;
                  vorlage.betreff = daten.betreff;
                  vorlage.vorschautext = daten.vorschautext;
                  nameAnzeige.textContent = daten.name;
                  alsGesichertMerken();
                  schliessen();
                  await pruefen();
                } catch (fehler) {
                  ersetzen(
                    meldungsBereich,
                    meldung(
                      'fehler',
                      fehler instanceof Error ? fehler.message : 'Speichern fehlgeschlagen',
                    ),
                  );
                }
              },
            }),
          ],
        });
      }

      // Beim Öffnen nur die schnelle Runde: der Zustand ist sofort sichtbar,
      // ohne dass jedes Öffnen des Editors Abrufe gegen fremde Server auslöst.
      // Die vollständige Prüfung läuft auf Knopfdruck und vor dem Versand.
      await pruefen({ netz: false });
    },
  };
}
